import { Dropdown, DropdownItem } from "@/design-system/Dropdown/Dropdown";
import { SortDropdown } from "@/design-system/Dropdown/SortDropdown";
import { InfoTip } from "@/design-system/InfoTip/InfoTip";
import {
  ExpandableSearch,
  useExpandableSearch,
} from "@/design-system/Input/ExpandableSearch";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import type { GalleryCategory } from "@repo/shared/api";
import {
  type GallerySort,
  type GalleryStatus,
  GALLERY_SORT_LABEL_KEYS as SORT_LABEL_KEYS,
  GALLERY_STATUS_LABEL_KEYS as STATUS_LABEL_KEYS,
} from "@repo/shared/gallery";
import { useTranslation } from "@repo/shared/i18n";
import { ArrowDown } from "lucide-react";
import { startTransition, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

/** Above the grid: a band with search and status, then category and sort under it. */
export const GalleryToolbar = ({
  searchTerm,
  onSearch,
  status,
  onStatusChanged,
  statusCounts,
  categoryControl,
  sortControl,
}: {
  searchTerm: string;
  onSearch: (searchTerm: string) => void;
  status: GalleryStatus;
  onStatusChanged: (status: GalleryStatus) => void;
  statusCounts: Record<GalleryStatus, number>;
  categoryControl?: React.ReactNode;
  sortControl?: React.ReactNode;
}) => {
  const { t } = useTranslation(["gallery", "common"]);
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  // The input owns its text, so a keystroke re-renders only the toolbar. The
  // page's filter runs as a transition that the next keystroke interrupts.
  const [searchInput, setSearchInput] = useState(searchTerm);
  const search = (value: string) => {
    setSearchInput(value);
    startTransition(() => onSearch(value));
  };
  // On a phone, as on /players: a button until tapped, then the band is the field's.
  const searchBox = useExpandableSearch(searchInput, isMobile);
  const dropdownWidth = isTouchDevice ? 118 : 150;
  const statusOptions: DropdownItem[] = (
    Object.keys(STATUS_LABEL_KEYS) as GalleryStatus[]
  ).map((value) => ({
    value,
    label: isTouchDevice
      ? t(STATUS_LABEL_KEYS[value])
      : `${t(STATUS_LABEL_KEYS[value])} (${statusCounts[value]})`,
  }));

  return (
    <div className="flex w-full flex-col">
      <div className="flex min-h-15 w-full flex-row justify-center bg-surface py-2 touch-device:py-4">
        <div className="mx-auto flex w-full max-w-content flex-wrap items-center gap-x-4 gap-y-2 px-4 max-touch-device:gap-x-2 large-desktop:px-8">
          <ExpandableSearch
            search={searchBox}
            value={searchInput}
            onChange={search}
            placeholder={t("common:search")}
            className="max-touch-device:w-auto max-touch-device:min-w-0 max-touch-device:flex-1"
          />
          <div
            className={cn(
              "ml-auto whitespace-nowrap text-sm",
              searchBox.takesOver && "hidden",
            )}
          >
            <Dropdown
              variant="secondary"
              hideSearch
              width={dropdownWidth}
              fitContent
              items={statusOptions}
              selected={status}
              placeholder={t(STATUS_LABEL_KEYS[status])}
              // Phones drop the "Status:" prefix for room.
              triggerLabel={
                isTouchDevice
                  ? t(STATUS_LABEL_KEYS[status])
                  : t("list.statusLabel", {
                      name: t(STATUS_LABEL_KEYS[status]),
                    })
              }
              onChange={(item) => onStatusChanged(item.value as GalleryStatus)}
            />
          </div>
        </div>
      </div>
      <div className="mx-auto flex w-full max-w-content items-center justify-between gap-x-4 whitespace-nowrap px-4 pt-4 text-sm max-touch-device:gap-x-2 large-desktop:px-8">
        {/* Shrinks first: a long category name truncates before the sort. */}
        <div className="min-w-0 shrink">{categoryControl}</div>
        <div className="ml-auto shrink-0">{sortControl}</div>
      </div>
    </div>
  );
};

/** The sort, right above the grid it orders. */
export const GallerySortControl = ({
  sort,
  onSortChanged,
  sortLockedHint,
}: {
  sort: GallerySort;
  onSortChanged: (sort: GallerySort) => void;
  /** Set while the sort can't change (an active plan): greys it out and says why on click. */
  sortLockedHint?: string;
}) => {
  const { t } = useTranslation("gallery");
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const sortOptions: DropdownItem[] = (
    Object.keys(SORT_LABEL_KEYS) as GallerySort[]
  ).map((value) => ({ value, label: t(SORT_LABEL_KEYS[value]) }));

  return sortLockedHint ? (
    <InfoTip
      className="-mr-1 items-center gap-x-1 px-1 py-1.5 text-sm text-foreground-faint hover:text-foreground-faint"
      icon={
        <>
          {t(SORT_LABEL_KEYS[sort])}
          <ArrowDown className="size-4" />
        </>
      }
    >
      {sortLockedHint}
    </InfoTip>
  ) : (
    <SortDropdown
      width={isTouchDevice ? 150 : 180}
      items={sortOptions}
      selected={sort}
      onChange={(value) => onSortChanged(value as GallerySort)}
    />
  );
};

/** The category filter, left of the sort above the grid. */
export const GalleryCategoryControl = ({
  categories,
  category,
  onCategoryChanged,
}: {
  /** "All" first. */
  categories: GalleryCategory[];
  category: GalleryCategory;
  onCategoryChanged: (category: GalleryCategory) => void;
}) => {
  const { t } = useTranslation("gallery");
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const dropdownWidth = isTouchDevice ? 118 : 150;
  const categoryOptions: DropdownItem[] = categories.map((c) => ({
    label: c.name,
    value: String(c.id),
  }));

  return (
    <Dropdown
      variant="secondary"
      hideSearch
      width={dropdownWidth}
      fitContent
      items={categoryOptions}
      selected={String(category.id)}
      placeholder={category.name}
      triggerLabel={
        isTouchDevice
          ? category.name
          : t("list.categoryLabel", { name: category.name })
      }
      onChange={(item) =>
        onCategoryChanged(
          categories.find((c) => String(c.id) === item.value) ?? category,
        )
      }
    />
  );
};
